import { Button, Input, Space, Switch } from 'antd';
import Modal from 'components/Modal';
import Page from 'components/Page';
import Table from 'components/Table';
import { useMutation } from 'hooks/apollo';
import useGlobalContext from 'hooks/useGlobalContext';
import useUser from 'hooks/useUser';
import React, { useCallback, useEffect, useState } from 'react';
import { EDIT_ACCESSIBILITY } from 'shared/constants/permissions';
import { scanColumns } from './columns';
import { useAccessibilityReview } from './context';
import { CREATE_WEBSITE_CHECK } from './graphql';
import { InactiveNotice, ReviewPage, REVIEW_CLASS, TABLE_SCROLL } from './ReviewPage';
import {
  defaultStartScanSelection,
  invalidScanUrls,
  scanUrlsFromText,
  websiteCheckInputsForStartScan,
} from './utils';

const SCAN_OPTIONS = [{
  key: 'accessibility',
  label: 'Accessibility',
  description: 'Scan with Squidler for accessibility issues.',
}, {
  key: 'pdf',
  label: 'Scan PDF documents',
  description: 'Analyse discoverable PDF documents, including files that download instead of opening in the browser.',
}, {
  key: 'brokenLinks',
  label: 'Check broken links',
  description: 'Find broken links during Extellio website checks.',
}];

const StartScanModal = ({ open, onClose }) => {
  const { website, scope, refetch } = useAccessibilityReview();
  const { message } = useGlobalContext();
  const { hasPermission } = useUser();
  const canStartAccessibility = Boolean(hasPermission(EDIT_ACCESSIBILITY));
  const [createWebsiteCheck, { loading: startingWebsiteCheck }] = useMutation(CREATE_WEBSITE_CHECK, {
    refetchQueries: ['GetAccessibilityWebsiteCheckStatuses'],
  });
  const [selection, setSelection] = useState(() => defaultStartScanSelection(website, {
    canStartAccessibility,
    pdfScanningEnabled: scope?.pdfScanningEnabled,
  }));
  const [customUrls, setCustomUrls] = useState('');
  useEffect(() => {
    if (!open) return;
    setSelection(defaultStartScanSelection(website, {
      canStartAccessibility,
      pdfScanningEnabled: scope?.pdfScanningEnabled,
    }));
    setCustomUrls('');
  }, [open, website, canStartAccessibility, scope?.pdfScanningEnabled]);

  const urls = scanUrlsFromText(customUrls);
  const invalidUrls = invalidScanUrls(urls);
  const websiteChecks = [
    selection.accessibility && 'ACCESSIBILITY',
    selection.brokenLinks && 'BROKEN_LINKS',
    selection.pdf && 'PDF',
  ].filter(Boolean);
  const canStart = Boolean(
    invalidUrls.length === 0
    && websiteChecks.length
    && website?.id,
  );
  const startScan = useCallback(async () => {
    const pages = scanUrlsFromText(customUrls);
    try {
      await Promise.all(websiteCheckInputsForStartScan({ selection, pages, website }).map(input => (
        createWebsiteCheck({
          variables: { websiteId: website.id, input },
        })
      )));
      message.success('Scan started.');
      onClose();
    } catch (error) {
      message.error(error.message);
    } finally {
      await refetch();
    }
  }, [
    createWebsiteCheck,
    customUrls,
    message,
    onClose,
    refetch,
    selection,
    website,
  ]);

  return (
    <Modal
      title="Start scan"
      open={open}
      onCancel={onClose}
      onOk={startScan}
      okText="Start scan"
      okButtonProps={{ disabled: !canStart, loading: startingWebsiteCheck }}
      fullWidth={500}
      rootClassName={REVIEW_CLASS}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <div>Choose what this scan should cover. Defaults match the current settings and are not saved.</div>
        {SCAN_OPTIONS.map((option) => {
          const pdfDisabled = option.key === 'pdf' && scope?.pdfScanningEnabled === false;
          const disabled = (option.key === 'accessibility' && !canStartAccessibility) || pdfDisabled;
          return (
            <Space key={option.key} direction="vertical" size={4} style={{ width: '100%' }}>
              <Space align="center" wrap>
                <Switch
                  id={`start-scan-${option.key}`}
                  aria-label={option.label}
                  checked={selection[option.key]}
                  disabled={disabled}
                  onChange={checked => setSelection(current => ({ ...current, [option.key]: checked }))}
                />
                <label htmlFor={`start-scan-${option.key}`}>{option.label}</label>
              </Space>
              <div>
                {pdfDisabled
                  ? 'Enable Scan PDF documents in Settings to start a PDF-only scan.'
                  : option.description}
              </div>
            </Space>
          );
        })}
        <div>Leave empty to scan the configured website. Or paste specific URLs, one per line.</div>
        <Input.TextArea
          value={customUrls}
          onChange={event => setCustomUrls(event.target.value)}
          autoSize={{ minRows: 5, maxRows: 12 }}
          placeholder={'https://example.com/page-one\nhttps://example.com/page-two'}
        />
        {invalidUrls.length > 0 && <div style={{ color: '#cf1322' }}>Use complete http or https URLs for every page.</div>}
      </Space>
    </Modal>
  );
};

const Scans = () => {
  const { scope, shellLoading } = useAccessibilityReview();
  const [startScanOpen, setStartScanOpen] = useState(false);
  const rows = scope?.scanHistory || [];

  return (
    <ReviewPage title="Scans">
      <Page.Content>
        {!shellLoading && !scope && <InactiveNotice />}
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          {scope && (
            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button type="primary" onClick={() => setStartScanOpen(true)}>New scan</Button>
            </div>
          )}
          <Table columns={scanColumns} dataSource={rows} loading={shellLoading} pagination={{ pageSize: 10 }} resizable={false} scroll={TABLE_SCROLL} />
        </Space>
      </Page.Content>
      <StartScanModal open={startScanOpen} onClose={() => setStartScanOpen(false)} />
    </ReviewPage>
  );
};

export default Scans;
